import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useT } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AiEmailItem, STATUS_LABEL } from "@/components/ai-email/AiEmailItem";
import { SimulateDialog } from "@/components/ai-email/SimulateDialog";
import { toast } from "sonner";

const TABS = ["pending_review", "auto_sent", "sent", "dismissed", "skipped", "error", "all"];
const fmt = (s) => (s ? String(s).slice(0, 16).replace("T", " ") : "—");

export default function AiInbox() {
  const { t } = useT();
  const qc = useQueryClient();
  const [tab, setTab] = useState("pending_review");
  const { data, isLoading } = useQuery({
    queryKey: ["ai-inbox", tab],
    queryFn: async () => (await api.get(`/ai-email/inbox?status=${tab}`)).data,
    refetchInterval: 30_000,
  });
  const { data: st } = useQuery({ queryKey: ["ai-email-settings"], queryFn: async () => (await api.get("/ai-email/settings")).data });
  const poll = useMutation({
    mutationFn: () => api.post("/ai-email/poll-now"),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["ai-inbox"] });
      const s = r.data;
      toast[s.error ? "error" : "success"](s.error ? `${t("Inbox check failed")}: ${s.error}` : `${t("Checked")} · ${s.new} ${t("new")} · ${s.auto_sent} ${t("auto-sent")} · ${s.held} ${t("held")}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const items = data?.items || [];
  const counts = data?.counts || {};
  const ready = st && (st.openai_key_set || st.using_emergent_fallback) && st.gmail_inbox_access && st.enabled;
  return (
    <div className="p-6 space-y-4" data-testid="ai-inbox-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">✉️ {t("AI Inbox")} <span className="text-slate-400 font-normal text-base">· {t("ChatGPT answers customers from CRM data")}</span></h1>
          <p className="text-sm text-slate-500">{t("Simple status / tracking / claim questions are answered automatically. Everything else waits here for you.")}</p>
          {st && (
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs" data-testid="ai-inbox-status">
              <Badge className={`border ${st.enabled ? "bg-emerald-50 text-emerald-800 border-emerald-300" : "bg-slate-100 text-slate-600 border-slate-300"}`} data-testid="ai-inbox-enabled">{st.enabled ? t("Assistant ON") : t("Assistant OFF")}</Badge>
              <Badge className={`border ${st.openai_key_set ? "bg-emerald-50 text-emerald-800 border-emerald-300" : st.using_emergent_fallback ? "bg-amber-50 text-amber-800 border-amber-300" : "bg-red-50 text-red-800 border-red-300"}`} data-testid="ai-inbox-key">
                {st.openai_key_set ? `${t("OpenAI key")} ${st.openai_key_hint}` : st.using_emergent_fallback ? t("Built-in key (add yours in Settings)") : t("No OpenAI key")}
              </Badge>
              <Badge className={`border ${st.gmail_inbox_access ? "bg-emerald-50 text-emerald-800 border-emerald-300" : "bg-red-50 text-red-800 border-red-300"}`} data-testid="ai-inbox-gmail">
                {st.gmail_inbox_access ? `Gmail ${st.gmail_email}` : t("Gmail inbox access missing")}
              </Badge>
              <span className="text-slate-500">{t("last check")} {fmt(data?.last_poll_at)}</span>
              <Link to="/settings#ai-email" className="underline text-slate-600" data-testid="ai-inbox-settings-link">{t("Settings")}</Link>
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <SimulateDialog />
          <Button onClick={() => poll.mutate()} disabled={poll.isPending || !st?.gmail_inbox_access} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="ai-inbox-poll-btn" title={st?.gmail_inbox_access ? "" : t("Grant Gmail inbox access in Settings first")}>
            {poll.isPending ? t("Checking…") : `📥 ${t("Check inbox now")}`}
          </Button>
        </div>
      </div>
      {st && !ready && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="ai-inbox-setup-hint">
          {t("Not fully set up yet")}: {[!st.enabled && t("turn the assistant ON"), !(st.openai_key_set || st.using_emergent_fallback) && t("add the OpenAI key"), !st.gmail_inbox_access && t("grant Gmail inbox access")].filter(Boolean).join(" · ")} — <Link to="/settings#ai-email" className="underline font-semibold">{t("open Settings")}</Link>. {t("You can already test with a pasted e-mail.")}
        </div>
      )}
      <div className="inline-flex flex-wrap rounded-md border border-slate-300 overflow-hidden text-sm" data-testid="ai-inbox-tabs">
        {TABS.map((k) => (
          <button key={k} type="button" onClick={() => setTab(k)} data-testid={`ai-inbox-tab-${k}`}
                  className={`px-3 py-1.5 font-semibold ${tab === k ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>
            {t(k === "all" ? "All" : STATUS_LABEL[k])}{k !== "all" && counts[k] ? ` (${counts[k]})` : ""}
          </button>
        ))}
      </div>
      {isLoading && <div className="text-slate-500 text-sm">{t("Loading…")}</div>}
      {!isLoading && items.length === 0 && (
        <div className="rounded-lg border border-dashed border-slate-300 p-10 text-center text-slate-400" data-testid="ai-inbox-empty">{t("Nothing here.")}</div>
      )}
      <div className="space-y-3">{items.map((m) => <AiEmailItem key={m.id} m={m} />)}</div>
    </div>
  );
}
